import React from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  Box,
  Text,
  VStack,
  HStack,
  Heading,
  Button,
  Grid,
  Image,
  Spinner,
  Alert,
  AlertIcon,
  Badge,
  Divider
} from '@chakra-ui/react';
import { ArrowBackIcon, ExternalLinkIcon } from '@chakra-ui/icons';
import { useProject } from '../hooks/useApi';

const ProjectDetail = () => {
  const { slug } = useParams();
  const { data: project, loading, error } = useProject(slug);

  if (loading) {
    return (
      <Box py={20} px={4}>
        <VStack spacing={8} maxW="5xl" mx="auto">
          <Spinner size="xl" color="green.500" />
          <Text color="gray.300">Loading project details...</Text>
        </VStack>
      </Box>
    );
  }

  if (error || !project) {
    return (
      <Box py={20} px={4}>
        <VStack spacing={8} maxW="5xl" mx="auto">
          <Alert status="error" bg="red.900" borderColor="red.600" border="1px solid">
            <AlertIcon />
            <VStack align="start" spacing={2}>
              <Text color="red.300" fontWeight="bold">
                Project Not Found
              </Text>
              <Text color="white">
                {error || "The project you're looking for doesn't exist or has been removed."}
              </Text>
            </VStack>
          </Alert>
          <Link to="/projects">
            <Button
              leftIcon={<ArrowBackIcon />}
              variant="ghost"
              color="green.500"
              _hover={{ color: "green.400" }}
            >
              Back to Projects
            </Button>
          </Link>
        </VStack>
      </Box>
    );
  }

  return (
    <Box py={20} px={4}>
      <VStack spacing={12} maxW="5xl" mx="auto" align="stretch">
        {/* Back Navigation */}
        <HStack>
          <Link to="/projects">
            <Button
              leftIcon={<ArrowBackIcon />}
              variant="ghost"
              color="green.500"
              _hover={{ color: "green.400" }}
              size="sm"
            >
              Back to Projects
            </Button>
          </Link>
        </HStack>

        {/* Project Header */}
        <VStack spacing={6} align="start">
          <HStack spacing={3}>
            <Box w={2} h={2} bg="green.500" borderRadius="full" />
            <Text color="green.500" fontSize="sm" textTransform="uppercase" letterSpacing="wide">
              {project.category_display}
            </Text>
            {project.is_featured && (
              <Badge colorScheme="green" variant="solid">
                Featured
              </Badge>
            )}
          </HStack>
          
          <Heading size="3xl" textTransform="uppercase" letterSpacing="wide">
            {project.title}
          </Heading>
          
          <Text fontSize="xl" color="gray.300" lineHeight="1.6">
            {project.short_description}
          </Text>
        </VStack>

        {/* Project Image */}
        {project.featured_image && (
          <Box
            w="100%"
            h="400px"
            bg="gray.800"
            border="1px solid"
            borderColor="gray.600"
            borderRadius="md"
            overflow="hidden"
          >
            <Image
              src={project.featured_image}
              alt={project.title}
              w="100%"
              h="100%"
              objectFit="cover"
            />
          </Box>
        )}

        {/* Project Info Grid */}
        <Grid templateColumns={{ base: "1fr", lg: "2fr 1fr" }} gap={12}>
          {/* Main Content */}
          <VStack spacing={8} align="start">
            {/* Detailed Description */}
            <VStack spacing={4} align="start" w="100%">
              <Heading size="lg" textTransform="uppercase" letterSpacing="wide">
                Project Overview
              </Heading>
              <Box
                color="gray.300"
                lineHeight="1.8"
                dangerouslySetInnerHTML={{ __html: project.detailed_description }}
                sx={{
                  '& p': { mb: 4 },
                  '& ul': { pl: 6, mb: 4 },
                  '& ol': { pl: 6, mb: 4 },
                  '& li': { mb: 2 },
                  '& h1, & h2, & h3, & h4, & h5, & h6': {
                    color: 'white',
                    fontWeight: 'bold',
                    mb: 3,
                    mt: 6
                  },
                  '& strong': { color: 'white' },
                  '& a': { color: 'green.400', textDecoration: 'underline' }
                }}
              />
            </VStack>

            {/* Gallery */}
            {project.gallery_images && project.gallery_images.length > 0 && (
              <VStack spacing={4} align="start" w="100%">
                <Heading size="lg" textTransform="uppercase" letterSpacing="wide">
                  Gallery
                </Heading>
                <Grid templateColumns="repeat(auto-fit, minmax(200px, 1fr))" gap={4} w="100%">
                  {project.gallery_images.map((image, index) => (
                    <Box
                      key={index}
                      h="200px"
                      bg="gray.800"
                      border="1px solid"
                      borderColor="gray.600"
                      borderRadius="md"
                      overflow="hidden"
                    >
                      <Image
                        src={image}
                        alt={`${project.title} gallery ${index + 1}`}
                        w="100%"
                        h="100%"
                        objectFit="cover"
                      />
                    </Box>
                  ))}
                </Grid>
              </VStack>
            )}
          </VStack>

          {/* Sidebar */}
          <VStack spacing={8} align="start">
            {/* Project Details */}
            <Box bg="gray.900" p={6} border="1px solid" borderColor="gray.700" w="100%">
              <VStack spacing={6} align="start">
                <Heading size="md" textTransform="uppercase" letterSpacing="wide">
                  Project Details
                </Heading>

                {/* Technologies */}
                {project.technologies_list && project.technologies_list.length > 0 && (
                  <VStack spacing={3} align="start" w="100%">
                    <Text fontSize="sm" fontWeight="bold" textTransform="uppercase" letterSpacing="wide">
                      Technologies
                    </Text>
                    <HStack wrap="wrap" spacing={2}>
                      {project.technologies_list.map((tech, index) => (
                        <Badge key={index} colorScheme="green" variant="solid">
                          {tech}
                        </Badge>
                      ))}
                    </HStack>
                  </VStack>
                )}

                {/* Client */}
                {project.client_name && (
                  <VStack spacing={2} align="start" w="100%">
                    <Text fontSize="sm" fontWeight="bold" textTransform="uppercase" letterSpacing="wide">
                      Client
                    </Text>
                    <Text color="gray.300">{project.client_name}</Text>
                  </VStack>
                )}

                {/* Completion Date */}
                {project.completion_date && (
                  <VStack spacing={2} align="start" w="100%">
                    <Text fontSize="sm" fontWeight="bold" textTransform="uppercase" letterSpacing="wide">
                      Completed
                    </Text>
                    <Text color="gray.300">
                      {new Date(project.completion_date).toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'long'
                      })}
                    </Text>
                  </VStack>
                )}

                <Divider borderColor="gray.600" />

                {/* Action Buttons */}
                <VStack spacing={3} w="100%">
                  {project.project_url && (
                    <Button
                      as="a"
                      href={project.project_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      rightIcon={<ExternalLinkIcon />}
                      bg="green.500"
                      color="white"
                      _hover={{ bg: "green.600" }}
                      w="100%"
                    >
                      View Live Project
                    </Button>
                  )}
                  
                  {project.github_url && (
                    <Button
                      as="a"
                      href={project.github_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      rightIcon={<ExternalLinkIcon />}
                      variant="outline"
                      borderColor="gray.600"
                      color="white"
                      _hover={{ borderColor: "green.500", color: "green.500" }}
                      w="100%"
                    >
                      View Source Code
                    </Button>
                  )}
                  
                  {project.case_study_url && (
                    <Button
                      as="a"
                      href={project.case_study_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      rightIcon={<ExternalLinkIcon />}
                      variant="outline"
                      borderColor="gray.600"
                      color="white"
                      _hover={{ borderColor: "green.500", color: "green.500" }}
                      w="100%"
                    >
                      Read Case Study
                    </Button>
                  )}
                </VStack>
              </VStack>
            </Box>

            {/* Contact CTA */}
            <Box bg="gray.900" p={6} border="1px solid" borderColor="gray.700" w="100%">
              <VStack spacing={4} align="start">
                <Heading size="md" textTransform="uppercase" letterSpacing="wide">
                  Interested in Similar Work?
                </Heading>
                <Text color="gray.300" fontSize="sm" lineHeight="1.6">
                  Let's discuss how we can help you build something amazing.
                </Text>
                <Link to="/contact" style={{ width: '100%' }}>
                  <Button
                    bg="green.500"
                    color="white"
                    _hover={{ bg: "green.600" }}
                    w="100%"
                  >
                    Start a Project
                  </Button>
                </Link>
              </VStack>
            </Box>
          </VStack>
        </Grid>
      </VStack>
    </Box>
  );
};

export default ProjectDetail;